import React from 'react';
import { Phone, Clock, CheckCircle, TrendingUp, Shield, Calendar } from 'lucide-react';
import { CTAButton } from '../common/CTAButton';

export function AboutHero() {
  return (
    <div className="py-16 bg-black/50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
            About Priority AI Services
          </h1>
          <div className="max-w-3xl mx-auto">
            <p className="text-xl text-gray-300 mb-6">
              I'm TJ, the owner of Priority AI Services. I help local service businesses stop losing money
              on missed calls and forgotten leads.
            </p>
            <p className="text-lg text-gray-400 mb-8">
              After working with small businesses and building practical systems, I saw the same pattern everywhere:
              busy owners missing opportunities because they couldn't respond fast enough. That's why I focus on simple,
              powerful systems that actually work.
            </p>
          </div>
        </div>

        <div className="mb-24">
          <div className="text-center mb-12">
            <h2 className="text-2xl font-bold text-white mb-4">What I Do</h2>
            <p className="text-lg text-gray-300 max-w-2xl mx-auto">
              I design and implement simple, powerful systems that stop businesses from losing money
              on missed calls and forgotten leads.
            </p>
          </div>
        </div>

        <div className="mb-24">
          <h2 className="text-2xl font-bold text-white mb-8 text-center">How I Work</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-black/40 border border-blue-500/20 rounded-lg p-6 text-center">
              <Phone className="w-10 h-10 text-blue-500 mx-auto mb-4" />
              <h3 className="text-lg font-bold text-white mb-2">15-Minute Audit Call</h3>
              <p className="text-gray-400 text-sm">
                Quick look at your current setup, no pressure
              </p>
            </div>

            <div className="bg-black/40 border border-blue-500/20 rounded-lg p-6 text-center">
              <Clock className="w-10 h-10 text-blue-500 mx-auto mb-4" />
              <h3 className="text-lg font-bold text-white mb-2">24-Hour Implementation</h3>
              <p className="text-gray-400 text-sm">
                Fast setup for Lead Rescue System, minimal disruption
              </p>
            </div>

            <div className="bg-black/40 border border-blue-500/20 rounded-lg p-6 text-center">
              <TrendingUp className="w-10 h-10 text-blue-500 mx-auto mb-4" />
              <h3 className="text-lg font-bold text-white mb-2">Light Monthly Fee</h3>
              <p className="text-gray-400 text-sm">
                System Care Plan plus optional add-ons
              </p>
            </div>

            <div className="bg-black/40 border border-blue-500/20 rounded-lg p-6 text-center">
              <Calendar className="w-10 h-10 text-blue-500 mx-auto mb-4" />
              <h3 className="text-lg font-bold text-white mb-2">Month-to-Month</h3>
              <p className="text-gray-400 text-sm">
                No long-term contracts, cancel anytime
              </p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {[
            {
              icon: <CheckCircle className="w-8 h-8" />,
              title: 'Reliability',
              description: 'Systems that work consistently so you can focus on serving clients'
            },
            {
              icon: <Phone className="w-8 h-8" />,
              title: 'Simplicity',
              description: 'No complicated software to learn - plugs into what you already use'
            },
            {
              icon: <TrendingUp className="w-8 h-8" />,
              title: 'Business Results',
              description: 'More booked appointments, fewer gaps in your schedule'
            },
            {
              icon: <Shield className="w-8 h-8" />,
              title: 'Transparency',
              description: 'Clear pricing, honest timelines, no hidden surprises'
            },
            {
              icon: <Clock className="w-8 h-8" />,
              title: 'Quick Implementation',
              description: 'Lead Rescue System setups complete within 24 hours of getting your details'
            },
            {
              icon: <Calendar className="w-8 h-8" />,
              title: 'Flexible Terms',
              description: 'Month-to-month plans with no long-term commitments'
            }
          ].map((value, index) => (
            <div key={index} className="p-6 rounded-lg border border-blue-500/20
              hover:border-blue-500/40 transition-all">
              <div className="text-blue-500 mb-4">{value.icon}</div>
              <h3 className="text-xl font-bold text-white mb-2">{value.title}</h3>
              <p className="text-gray-400">{value.description}</p>
            </div>
          ))}
        </div>

        <div className="text-center">
          <CTAButton href="/book" variant="primary" icon>
            Book Your Free Lead Rescue Audit
          </CTAButton>
        </div>
      </div>
    </div>
  );
}